import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiPlus, PiTrash } from "react-icons/pi";
import { ArrowRight, Calendar, MapPin } from "lucide-react";

const EVENT_IMAGES = [
  "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600&h=400&fit=crop",
  "https://images.unsplash.com/photo-1511578314322-379afb476865?w=600&h=400&fit=crop",
  "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=600&h=400&fit=crop",
];

export default function UniversityEvents({ content, onContentChange, isSelected }: SectionContentProps) {
  const safeContent = content || {};
  const events = safeContent.events || [
    { title: "Edu Fest 2023: Igniting Minds, Transforming Lives", date: "Nov 11, 2023", location: "Yarra Park, UK", link: "#" },
    { title: "Sustainability & Development Showcase: Green Living", date: "Nov 11, 2023", location: "Yarra Park, UK", link: "#" },
    { title: "Career Carnival: Explore Your Professional Journey", date: "Nov 11, 2023", location: "Yarra Park, UK", link: "#" },
  ];

  const addEvent = () => {
    const newEvents = [...events, { title: "New Event", date: "Jan 1, 2024", location: "Location", link: "#" }];
    onContentChange?.({ ...safeContent, events: newEvents });
  };

  const removeEvent = (idx: number) => {
    const newEvents = events.filter((_: any, i: number) => i !== idx);
    onContentChange?.({ ...safeContent, events: newEvents });
  };

  const updateEvent = (idx: number, field: string, value: string) => {
    const newEvents = [...events];
    newEvents[idx] = { ...newEvents[idx], [field]: value };
    onContentChange?.({ ...safeContent, events: newEvents });
  };

  return (
    <section className="py-20 px-4">
      <div className="mx-auto max-w-7xl">
        {/* Section Header */}
        <div className="mb-10 flex items-end justify-between border-b border-gray-200 pb-6">
          <h2 className="text-3xl font-bold text-[#1A1A1A]">
            <EditableText
              value={safeContent.title || "Upcoming Events"}
              onChange={(val) => onContentChange?.({ ...safeContent, title: val })}
              as="span"
              placeholder="Section Title"
            />
          </h2>
          <a
            href={safeContent.viewAllLink || "#"}
            className="group flex items-center gap-2 text-sm font-medium text-[#FF6D1C] hover:text-[#e55a0f] transition-colors"
          >
            View All
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </a>
        </div>

        {/* Events Grid */}
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
          {events.map((event: any, idx: number) => (
            <div
              key={idx}
              className="group rounded-xl bg-white shadow-md transition-all hover:shadow-xl"
              style={{ position: "relative" }}
            >
              {isSelected && (
                <button
                  onClick={() => removeEvent(idx)}
                  className="absolute -top-2 -right-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-white hover:bg-red-600"
                >
                  <PiTrash className="h-3 w-3" />
                </button>
              )}

              {/* Event Image */}
              <div className="relative overflow-hidden rounded-t-xl">
                <img
                  src={EVENT_IMAGES[idx % EVENT_IMAGES.length]}
                  alt={event.title}
                  className="h-48 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>

              {/* Event Meta */}
              <div className="p-6">
                <div className="mb-4 flex flex-wrap gap-4 text-sm text-[#6c7279]">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4 text-[#FF6D1C]" />
                    <EditableText
                      value={event.date}
                      onChange={(val) => updateEvent(idx, "date", val)}
                      as="span"
                      placeholder="Date"
                    />
                  </span>
                  <span className="flex items-center gap-1">
                    <MapPin className="h-4 w-4 text-[#FF6D1C]" />
                    <EditableText
                      value={event.location}
                      onChange={(val) => updateEvent(idx, "location", val)}
                      as="span"
                      placeholder="Location"
                    />
                  </span>
                </div>

                <h5 className="mb-4 text-lg font-bold text-[#1A1A1A] leading-snug">
                  <a href={event.link || "#"}>
                    <EditableText
                      value={event.title}
                      onChange={(val) => updateEvent(idx, "title", val)}
                      as="span"
                      placeholder="Event Title"
                    />
                  </a>
                </h5>

                <a
                  href={event.link || "#"}
                  className="inline-flex items-center gap-2 text-sm font-medium text-[#FF6D1C] hover:text-[#e55a0f] transition-colors"
                >
                  Learn More
                  <ArrowRight className="h-4 w-4" />
                </a>
              </div>
            </div>
          ))}

          {isSelected && (
            <button
              onClick={addEvent}
              className="flex min-h-[400px] flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 text-gray-400 hover:border-[#FF6D1C] hover:bg-orange-50 hover:text-[#FF6D1C] transition-all"
            >
              <PiPlus className="h-10 w-10 mb-2" />
              <span className="text-sm font-medium">Add Event</span>
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
